import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Lock, Check, Flame, Clock, ImageIcon, Users, Star, Ticket } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_BG = '#FFD400'; // must match the ProximosCupones page background (ticket notches)

// Default product thumbnails used when a coupon has no custom images.
const DEFAULT_UPCOMING_IMAGES = [
  'https://customer-assets-m6fa6gv7.emergentagent.net/job_panda-deals-mx/artifacts/ugl7w0qu_image.png',
  'https://customer-assets-m6fa6gv7.emergentagent.net/job_panda-deals-mx/artifacts/qfciiw6y_image.png',
  'https://customer-assets-m6fa6gv7.emergentagent.net/job_panda-deals-mx/artifacts/jmzqyf4y_image.png',
  'https://customer-assets-m6fa6gv7.emergentagent.net/job_panda-deals-mx/artifacts/4y2vix5o_image.png',
];

const two = (n) => String(n).padStart(2, '0');

const formatCountdown = (ms) => {
  if (ms <= 0) return '00:00:00';
  const t = Math.floor(ms / 1000);
  const d = Math.floor(t / 86400);
  const h = Math.floor((t % 86400) / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return d > 0 ? `${d}d ${two(h)}:${two(m)}:${two(s)}` : `${two(h)}:${two(m)}:${two(s)}`;
};

const formatHs = (date) => `${two(date.getHours())}:${two(date.getMinutes())}hs`;

const UpcomingCouponCard = ({ coupon, settings = {} }) => {
  const [now, setNow] = useState(Date.now());
  const interestedKey = `op_interesa_${coupon.id}`;
  const [interested, setInterested] = useState(() => {
    try { return localStorage.getItem(interestedKey) === '1'; } catch { return false; }
  });
  const [interactions, setInteractions] = useState(coupon.clicks || 0);
  const [showGoModal, setShowGoModal] = useState(false);
  const [goCount, setGoCount] = useState(4);
  const navTimerRef = useRef(null);
  const couponLink = coupon.affiliate_link || settings.affiliate_link || 'https://mercadolibre.com/sec/1ppLzNT';

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // "Ir al cupón" countdown pop-up (4..1) then navigate in the SAME tab.
  useEffect(() => {
    if (!showGoModal) return undefined;
    if (goCount <= 0) {
      window.location.href = couponLink;
      return undefined;
    }
    navTimerRef.current = setTimeout(() => setGoCount((c) => c - 1), 1000);
    return () => { if (navTimerRef.current) clearTimeout(navTimerRef.current); };
  }, [showGoModal, goCount, couponLink]);

  // On BFCache restore / returning from Mercado Libre, cancel any pending navigation
  // and close the pop-up so it doesn't auto-redirect again (avoids blank-page loop).
  useEffect(() => {
    const reset = () => {
      if (navTimerRef.current) { clearTimeout(navTimerRef.current); navTimerRef.current = null; }
      setShowGoModal(false);
      setGoCount(4);
    };
    const onPageShow = () => reset();
    const onVisibility = () => { if (document.visibilityState === 'visible') reset(); };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const availableDate = coupon.available_at ? new Date(coupon.available_at) : null;
  const msLeft = availableDate ? availableDate.getTime() - now : -1;

  let status;
  if (coupon.manual_status === 'finalizado') status = 'finalizado';
  else if (coupon.manual_status === 'disponible') status = 'disponible';
  else if (coupon.manual_status === 'proximo') status = 'proximo';
  else status = availableDate && msLeft <= 0 ? 'disponible' : 'proximo';

  const finalizado = status === 'finalizado';
  const disponible = status === 'disponible';

  const handleInteresa = () => {
    if (interested) return;
    setInterested(true);
    setInteractions((n) => n + 1);
    try { localStorage.setItem(interestedKey, '1'); } catch { /* ignore */ }
    axios.post(`${API}/coupons/${coupon.id}/click`).catch(() => {});
  };

  const handleGoToCoupon = () => {
    setGoCount(4);
    setShowGoModal(true);
  };

  const handleGoNow = () => {
    if (navTimerRef.current) { clearTimeout(navTimerRef.current); navTimerRef.current = null; }
    window.location.href = couponLink;
  };

  const secondary = coupon.secondary_text || 'Próximo cupón';
  const customImages = Array.isArray(coupon.images) ? coupon.images.filter(Boolean).slice(0, 4) : [];
  const images = customImages.length ? customImages : DEFAULT_UPCOMING_IMAGES;
  const thumbSlots = Array.from({ length: 4 }).map((_, i) => images[i] || null);

  return (
    <div
      data-testid={`upcoming-card-${coupon.id}`}
      className={`relative flex rounded-[22px] overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.12)] ${finalizado ? 'opacity-90' : ''}`}
      style={{ backgroundColor: finalizado ? '#e9ebf0' : '#f6f7f9' }}
    >
      {/* ===== LEFT PANEL ===== */}
      <div className={`flex-1 min-w-0 p-4 sm:p-5 flex flex-col gap-3 ${finalizado ? 'grayscale' : ''}`}>
        <div>
          <p className={`font-fredoka text-3xl sm:text-4xl font-black leading-none tracking-tight ${finalizado ? 'text-slate-400' : 'text-slate-900'}`}>
            {coupon.title}
          </p>
          <p className={`text-xs font-extrabold uppercase tracking-widest mt-1 ${finalizado ? 'text-slate-400' : 'text-slate-500'}`}>
            {disponible ? <span className="text-[#1479FF]">Disponible ahora</span> : secondary}
          </p>
          {interactions > 0 && !finalizado && (
            <span
              className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-[11px] font-bold px-2 py-0.5 rounded-full mt-1.5"
              data-testid={`upcoming-interactions-${coupon.id}`}
            >
              <Users className="w-3 h-3" /> {interactions} interesados
            </span>
          )}
        </div>

        {/* Product thumbnails */}
        <div className="flex items-center gap-2">
          {thumbSlots.map((src, i) => (
            <div
              key={i}
              className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center overflow-hidden ${src ? 'bg-white shadow-sm' : 'bg-slate-200/70'}`}
            >
              {src
                ? <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain p-1" />
                : <ImageIcon className="w-5 h-5 text-slate-300" />}
            </div>
          ))}
        </div>

        {/* Footer line */}
        <div className="flex items-end justify-between gap-2 mt-auto pt-1">
          {coupon.max_discount != null ? (
            <span className="text-sm font-bold text-slate-400">
              Tope $ {Number(coupon.max_discount).toLocaleString('es-MX')}
            </span>
          ) : <span />}
          {coupon.min_purchase != null && (
            <span className="text-sm font-semibold text-slate-500" data-testid={`upcoming-min-${coupon.id}`}>
              Compra mínima: ${Number(coupon.min_purchase).toLocaleString('es-MX')}
            </span>
          )}
        </div>
      </div>

      {/* ===== TICKET DIVIDER + NOTCHES ===== */}
      <div className="relative w-28 sm:w-40 shrink-0 flex flex-col items-center justify-center p-3 sm:p-4 text-center">
        <span className="absolute left-0 top-4 bottom-4 border-l-2 border-dashed border-slate-300" />
        <span className="absolute -left-[10px] -top-[10px] w-5 h-5 rounded-full" style={{ backgroundColor: PAGE_BG }} />
        <span className="absolute -left-[10px] -bottom-[10px] w-5 h-5 rounded-full" style={{ backgroundColor: PAGE_BG }} />

        {/* Icon */}
        <div className="relative mb-2">
          {disponible ? (
            <div className="w-12 h-12 rounded-2xl bg-[#1479FF] flex items-center justify-center shadow-md">
              <Flame className="w-6 h-6 text-white" />
            </div>
          ) : (
            <div className="relative">
              <Lock className={`w-10 h-10 ${finalizado ? 'text-slate-400' : 'text-amber-400'}`} fill="currentColor" strokeWidth={1.5} />
              {!finalizado && (
                <span className="absolute -bottom-1 -right-1.5 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow">
                  <Clock className="w-3.5 h-3.5 text-[#1479FF]" strokeWidth={2.5} />
                </span>
              )}
            </div>
          )}
        </div>

        {/* Time / label */}
        {finalizado ? (
          <p className="text-sm font-black text-slate-400 uppercase">Finalizado</p>
        ) : disponible ? (
          <p className="text-sm font-extrabold text-[#1479FF] leading-tight">¡Ya disponible!</p>
        ) : availableDate ? (
          <>
            <p className="text-[11px] font-semibold text-slate-500 leading-none">Disponible a las</p>
            <p className="text-xl sm:text-2xl font-black text-slate-900 leading-tight" data-testid={`upcoming-time-${coupon.id}`}>
              {formatHs(availableDate)}
            </p>
            <p className="text-[11px] font-bold text-[#1479FF] mt-0.5 tabular-nums" data-testid={`upcoming-countdown-${coupon.id}`}>
              {formatCountdown(msLeft)}
            </p>
          </>
        ) : (
          <p className="text-sm font-bold text-slate-500">Próximamente</p>
        )}

        {/* Button */}
        {finalizado ? null : disponible ? (
          <button
            onClick={handleGoToCoupon}
            data-testid={`upcoming-go-${coupon.id}`}
            className="mt-2 w-full h-9 rounded-lg flex items-center justify-center gap-1.5 font-extrabold text-xs bg-[#1479FF] text-white hover:bg-[#0f63d6] transition-colors active:scale-95"
          >
            <Flame className="w-3.5 h-3.5" /> Ir al cupón
          </button>
        ) : (
          <button
            onClick={handleInteresa}
            disabled={interested}
            data-testid={`upcoming-interesa-${coupon.id}`}
            className={`mt-2 w-full h-9 rounded-lg flex items-center justify-center gap-1.5 font-extrabold text-xs transition-colors active:scale-95 ${
              interested
                ? 'bg-emerald-500 text-white cursor-default'
                : 'bg-[#1479FF] text-white hover:bg-[#0f63d6]'
            }`}
          >
            {interested ? <><Check className="w-3.5 h-3.5" /> Me interesa</> : <><Star className="w-3.5 h-3.5" /> Me interesa</>}
          </button>
        )}
      </div>

      {/* "Ir al cupón" info pop-up with 4s countdown */}
      {showGoModal && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          data-testid={`upcoming-go-modal-overlay-${coupon.id}`}
        >
          <div
            className="relative w-full max-w-xs rounded-3xl bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            data-testid={`upcoming-go-modal-${coupon.id}`}
          >
            <div className="bg-[#FFD400] px-5 py-3 flex items-center gap-2">
              <Ticket className="w-5 h-5 text-slate-900" />
              <p className="font-fredoka text-lg font-black text-slate-900">Antes de continuar</p>
            </div>
            <div className="p-5 text-center">
              <p className="text-sm text-slate-600">Cuando se abra Mercado Libre sigue esta ruta:</p>
              <div className="my-3 rounded-xl bg-[#1479FF] text-white px-3 py-3">
                <p className="font-extrabold text-sm leading-snug">{settings.upcoming_route_steps || 'Más → Cupones →'}</p>
                <p className="font-black text-base leading-snug">{settings.upcoming_route_highlight || 'Banner 10/10 con Belinda'}</p>
              </div>
              <p className="text-sm text-slate-600">Ahí encontrarás los <strong>Cupones de rescate</strong> disponibles.</p>
              <p className="mt-4 text-sm font-bold text-slate-500 flex items-center justify-center gap-1.5">
                <Clock className="w-4 h-4 text-[#1479FF]" />
                Abriendo Mercado Libre en <span className="text-[#1479FF] tabular-nums">{goCount}</span> segundos...
              </p>
              <button
                onClick={handleGoNow}
                data-testid={`upcoming-go-now-${coupon.id}`}
                className="mt-4 w-full h-11 rounded-xl flex items-center justify-center gap-1.5 font-extrabold text-sm bg-[#1479FF] text-white hover:bg-[#0f63d6] transition-colors active:scale-95"
              >
                Ir ahora →
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UpcomingCouponCard;
